import { useCallback, useEffect, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse } from "@entrilia/shared/api/types";
import { logError } from "@entrilia/shared/logging";

interface UseFavoriteReportsParams {
  clientCode: string;
  getFavoriteReports: (clientCode: string) => Promise<ApiResponse<string[]>>;
  toggleFavoriteReport: (clientCode: string, reportId: string) => Promise<ApiResponse<string[]>>;
}

interface UseFavoriteReportsResult {
  favoriteIds: Set<string>;
  toggleFavorite: (reportId: string) => void;
}

const withToggledId = (ids: Set<string>, id: string): Set<string> => {
  const next = new Set(ids);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
};

const useFavoriteReports = ({
  clientCode,
  getFavoriteReports,
  toggleFavoriteReport,
}: UseFavoriteReportsParams): UseFavoriteReportsResult => {
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [ids, error] = await withErrorHandling(getFavoriteReports)(clientCode);
      if (error) {
        logError(error, "[useFavoriteReports] getFavoriteReports");
        return;
      }
      if (!cancelled) {
        setFavoriteIds(new Set(ids));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [clientCode, getFavoriteReports]);

  const toggleFavorite = useCallback(
    (reportId: string) => {
      setFavoriteIds((prev) => withToggledId(prev, reportId));

      withErrorHandling(toggleFavoriteReport)(clientCode, reportId).then(([, error]) => {
        if (error) {
          logError(error, "[useFavoriteReports] toggleFavoriteReport");
          setFavoriteIds((prev) => withToggledId(prev, reportId));
        }
      });
    },
    [clientCode, toggleFavoriteReport]
  );

  return { favoriteIds, toggleFavorite };
};

export default useFavoriteReports;
